import { useEffect, useRef, useState } from "react";
import type { CaptionToken } from "../../Helper/KaiVoiceCaption";

interface KaiVoiceCaptionStageProps {
  /** Changes with every sentence: the old one fades out, the new one starts hidden. */
  sentenceKey: string;
  tokens: CaptionToken[];
  /** Words of the sentence Kai has said. */
  spoken: number;
  /** Harness mode: no pacing, every spoken word shown at once. */
  instant?: boolean;
}

// The transcript arrives in bursts (and a pause completes the sentence at
// once): words are revealed one after another at speaking pace instead.
const REVEAL_STEP_MS = 90;
const CATCH_UP_STEP_MS = 45;
const LEAVE_MS = 420;
// The caption area fades out over its bottom 40 px.
const BOTTOM_FADE_PX = 40;

const useSteadyReveal = (target: number, sentenceKey: string, instant: boolean): number => {
  const [reveal, setReveal] = useState({ sentenceKey, shown: instant ? target : 0 });
  const shown = reveal.sentenceKey === sentenceKey ? Math.min(reveal.shown, target) : 0;

  useEffect(() => {
    if (instant) {
      setReveal({ sentenceKey, shown: target });
      return;
    }
    if (shown >= target) return;
    const timer = setTimeout(
      () => setReveal({ sentenceKey, shown: shown + 1 }),
      target - shown > 2 ? CATCH_UP_STEP_MS : REVEAL_STEP_MS,
    );
    return () => clearTimeout(timer);
  }, [instant, sentenceKey, shown, target]);

  return instant ? target : shown;
};

const renderWords = (tokens: CaptionToken[], shown: number) =>
  tokens.map((token, index) => (
    <span key={index} className={index < shown ? "kai-voice-call--word kai-voice-call--word-shown" : "kai-voice-call--word"}>
      {token.text + token.gap}
    </span>
  ));

/**
 * The sentence Kai is saying. It is laid out in full from the start (unsaid
 * words are transparent), so line breaks and centering never move while the
 * words fade in; the previous sentence fades out on top of it.
 */
export const KaiVoiceCaptionStage = ({ sentenceKey, tokens, spoken, instant = false }: KaiVoiceCaptionStageProps) => {
  const shown = useSteadyReveal(spoken, sentenceKey, instant);
  const [leaving, setLeaving] = useState<{ sentenceKey: string; tokens: CaptionToken[]; shown: number } | null>(null);
  const lastRef = useRef({ sentenceKey, tokens, shown });
  const currentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const last = lastRef.current;
    lastRef.current = { sentenceKey, tokens, shown };
    if (last.sentenceKey === sentenceKey || last.shown === 0) return;
    // Kai moved on to the next sentence: the previous one was said to its end, even
    // where the steady reveal had not caught up yet.
    setLeaving(spoken > 0 ? { ...last, shown: last.tokens.length } : last);
  }, [sentenceKey, tokens, shown, spoken]);

  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => setLeaving(null), LEAVE_MS);
    return () => clearTimeout(timer);
  }, [leaving]);

  // A sentence longer than the caption area: the words being said stay in
  // view (it is laid out in full, so its invisible end is what overflows).
  useEffect(() => {
    const current = currentRef.current;
    const area = current?.closest<HTMLElement>(".kai-voice-call--captions");
    if (!current || !area) return;
    if (shown === 0) {
      area.scrollTop = 0;
      return;
    }
    const word = current.children[shown - 1] as HTMLElement | undefined;
    if (!word) return;
    const overflow = word.getBoundingClientRect().bottom - (area.getBoundingClientRect().bottom - BOTTOM_FADE_PX);
    if (overflow > 0) area.scrollTop += overflow;
  }, [sentenceKey, shown]);

  return (
    <div className="kai-voice-call--caption-stage" aria-hidden="true">
      {leaving && (
        <div className="kai-voice-call--caption kai-voice-call--caption-leaving" key={leaving.sentenceKey}>
          {renderWords(leaving.tokens, leaving.shown)}
        </div>
      )}
      <div className="kai-voice-call--caption" key={sentenceKey} ref={currentRef}>
        {renderWords(tokens, shown)}
      </div>
    </div>
  );
};
